import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Circus Tent — the fairground's big top: a plump striped red-and-cream drum
// wearing a swooping conical roof, a scalloped valance skirting the eaves, a
// wide pulled-open entrance flap at the front and a pennant flag snapping on
// the peak. The flag is named "flag" so it waves; the whole crown (cone,
// stripes, finial and pole) is named "roof" so the tent can breathe — a very
// subtle canvas swell, like the show inside is exhaling.
//
// Frame: y = 0 is the ground, the entrance faces +Z. 1 unit = 1 m; the tent
// is a squat ~4.5 m big top on a 3x3 tile plot.

const RED = "#d8412f";        // big-top red canvas
const RED_DARK = "#a92e22";   // shaded canvas / the dark entrance mouth
const CREAM = "#f6efe3";      // cream canvas stripes and trims
const CREAM_DARK = "#e4d9c4"; // shaded cream (wall drum base tone)
const GOLD = "#e8b53a";       // finial ball and entrance trim
const TEAL = "#3f9e9b";       // pennant flag

// ── frame heights ────────────────────────────────────────────────────────────
const WALL_H = 1.85;                 // canvas drum, ground to eaves
const WALL_R = 2.55;                 // drum radius (slight taper below)
const ROOF_R = 2.9;                  // cone base radius, overhanging the walls
const ROOF_H = 2.35;                 // cone rise, eaves to apex (apex at 4.2)
const APEX_Y = WALL_H + ROOF_H;
const POLE_TOP = APEX_Y + 0.55;      // flag pole tip (~4.55)

// The crown breathes about the eaves line; the flag waves about the pole.
const ROOF_PIVOT: [number, number, number] = [0, WALL_H, 0];
const FLAG_PIVOT: [number, number, number] = [0, POLE_TOP - 0.12, 0];

// Red stripes standing on the cream wall drum, skipping the entrance arc so
// they never crowd the door. Leaned to hug the drum's slight taper.
function wallStripes(): Part[] {
  const out: Part[] = [];
  const n = 12;
  for (let i = 0; i < n; i++) {
    const az = (i / n) * Math.PI * 2 + Math.PI / n;
    if (Math.cos(az) > 0.82) continue; // leave the front clear for the entrance
    out.push({
      geometry: new THREE.BoxGeometry(0.42, WALL_H, 0.1),
      color: RED,
      position: [Math.sin(az) * (WALL_R + 0.01), WALL_H / 2, Math.cos(az) * (WALL_R + 0.01)],
      rotation: [0.03, az, 0],
      eulerOrder: "YXZ",
    });
  }
  return out;
}

// Cream stripes running down the red cone, each lying along the slope. All
// named "roof" so they breathe with the crown.
function roofStripes(): Part[] {
  const out: Part[] = [];
  const tilt = Math.atan2(ROOF_R, ROOF_H);  // slope lean from vertical
  const slant = Math.hypot(ROOF_R, ROOF_H);
  for (let i = 0; i < 8; i++) {
    const az = (i / 8) * Math.PI * 2 + Math.PI / 8;
    // Centre of the slope, nudged out along the surface normal so the stripe
    // sits just proud of the red canvas. The negative tilt leans the stripe's
    // top INWARD toward the apex, hugging the cone.
    const radial = ROOF_R * 0.5 + 0.04;
    out.push({
      geometry: new THREE.BoxGeometry(0.46, slant * 0.8, 0.08),
      color: CREAM,
      position: [Math.sin(az) * radial, WALL_H + ROOF_H * 0.5 + 0.05, Math.cos(az) * radial],
      rotation: [-tilt, az, 0],
      eulerOrder: "YXZ",
      name: "roof",
    });
  }
  return out;
}

// The scalloped valance: plump half-round tabs hanging under the roof's
// overhang, alternating red and cream around the whole rim.
function scallops(): Part[] {
  const out: Part[] = [];
  const n = 16;
  for (let i = 0; i < n; i++) {
    const az = (i / n) * Math.PI * 2;
    out.push({
      geometry: new THREE.SphereGeometry(0.32, 10, 8),
      color: i % 2 === 0 ? CREAM : RED,
      position: [Math.sin(az) * (ROOF_R - 0.06), WALL_H - 0.1, Math.cos(az) * (ROOF_R - 0.06)],
      rotation: [0, az, 0],
      scale: [1, 0.85, 0.38],
    });
  }
  return out;
}

// The entrance: a dark canvas mouth sunk into the front wall, flanked by two
// flaps pulled open and pinned back, under a little gold trim bar.
function entrance(): Part[] {
  return [
    // The dark opening.
    { geometry: new THREE.BoxGeometry(1.35, 1.5, 0.3), color: RED_DARK,
      position: [0, 0.75, 2.48] },
    // Pulled-back flaps, hinged at the opening's outer edges — red so they
    // pop against the cream drum.
    { geometry: new THREE.BoxGeometry(0.62, 1.55, 0.08), color: RED,
      position: [-0.95, 0.78, 2.52], rotation: [0, 0.85, 0] },
    { geometry: new THREE.BoxGeometry(0.62, 1.55, 0.08), color: RED,
      position: [0.95, 0.78, 2.52], rotation: [0, -0.85, 0] },
    // A gold trim bar crowning the doorway.
    { geometry: new THREE.BoxGeometry(1.55, 0.15, 0.15), color: GOLD,
      position: [0, 1.6, 2.55] },
  ];
}

export const circusTent = defineModel({
  id: "circus-tent",
  name: "Circus Tent",
  batchId: "mess",
  tags: ["mess", "cute", "animated", "fairground", "circus", "tent", "big-top", "building"],
  summary: "A striped red-and-cream big top with a scalloped skirt, open entrance flap and a waving peak pennant.",
  description:
    "The fairground's big top: a plump cream canvas drum with red stripes, a " +
    "swooping striped cone roof edged in a scalloped valance, a wide entrance " +
    "flap pulled open at the front and a teal pennant snapping on the peak " +
    "pole. The flag waves on idle and the whole crown breathes with a very " +
    "subtle canvas swell.",
  author: "models",
  footprint: 3.1,
  tiles: { w: 3, h: 3 },
  parts(): Part[] {
    return [
      // ── the canvas drum ───────────────────────────────────────────────────
      { geometry: new THREE.CylinderGeometry(WALL_R, WALL_R + 0.12, WALL_H, 22),
        color: CREAM, position: [0, WALL_H / 2, 0] },
      // A shaded ring seating the drum on the ground.
      { geometry: new THREE.CylinderGeometry(WALL_R + 0.16, WALL_R + 0.24, 0.18, 22),
        color: CREAM_DARK, position: [0, 0.09, 0] },
      ...wallStripes(),

      // ── the crown (all named "roof" so it breathes as one) ────────────────
      { geometry: new THREE.ConeGeometry(ROOF_R, ROOF_H, 22), color: RED,
        position: [0, WALL_H + ROOF_H / 2, 0], name: "roof" },
      ...roofStripes(),
      // Peak pole and golden finial ball.
      { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.6, 8), color: RED_DARK,
        position: [0, APEX_Y + 0.28, 0], name: "roof" },
      { geometry: new THREE.SphereGeometry(0.13, 10, 8), color: GOLD,
        position: [0, APEX_Y + 0.02, 0], name: "roof" },

      // ── the scalloped valance ─────────────────────────────────────────────
      ...scallops(),

      // ── the entrance (facing +Z) ──────────────────────────────────────────
      ...entrance(),

      // ── the pennant flag (named "flag" so it waves) ───────────────────────
      { geometry: new THREE.ConeGeometry(0.17, 0.62, 4), color: TEAL,
        position: [0.34, POLE_TOP - 0.12, 0], rotation: [0, 0, -Math.PI / 2],
        scale: [1, 1, 0.18], name: "flag" },
    ];
  },
  animations: [
    // Idle — the peak pennant swishes back and forth in the breeze.
    {
      id: "idle",
      label: "Pennant waving",
      duration: 2.6,
      loop: true,
      tracks: [
        {
          target: "flag",
          pivot: FLAG_PIVOT,
          rotation: [
            { t: 0, value: [0, 0.4, 0.04], ease: "sine" },
            { t: 1.3, value: [0, -0.4, -0.04], ease: "sine" },
            { t: 2.6, value: [0, 0.4, 0.04], ease: "sine" },
          ],
        },
      ],
    },
    // Breathe — the signature move: the whole canvas crown swells and settles
    // about the eaves, very subtly, like the show inside is exhaling. The flag
    // rides the apex so it never detaches from the pole tip.
    {
      id: "breathe",
      label: "Tent breathing",
      duration: 4.2,
      loop: true,
      tracks: [
        {
          target: "roof",
          pivot: ROOF_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 2.1, value: [1.015, 1.025, 1.015], ease: "sine" },
            { t: 4.2, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "flag",
          pivot: FLAG_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 2.1, value: [0, 0.07, 0], ease: "sine" },
            { t: 4.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
